import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';

import { ApiError, createAccountApi, onContractFailure, onSecureFailure, onSubscriptionConfirmed } from '../api/client';
import type { Envelope, User, Workspace } from '../api/contract';
import { captureAuthEpoch, isAuthUnknown, isCurrentAuthEpoch, subscribeAuthChange } from './auth-context';
import { safeReturnTo } from '../features/auth/returnTo';
import { useSubscription } from './subscriptions/useSubscription';
import { SubscriptionPage } from './subscriptions/SubscriptionPage';

interface AppContextValue {
  api: ReturnType<typeof createAccountApi>;
  user: User;
  workspaces: Workspace[];
  workspace?: Workspace;
  subscriptionEnabled?: boolean;
  setWorkspaceId: (id: string) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const epoch = useSyncExternalStore(subscribeAuthChange, captureAuthEpoch);
  if (isAuthUnknown()) return <StatusScreen title="Финансовые данные скрыты: вход не подтверждён" actionHref="/auth/email/login" actionLabel="Повторить вход" />;
  return <AccountDataProvider key={epoch} epoch={epoch}>{children}</AccountDataProvider>;
}

function AccountDataProvider({ children, epoch }: { children: ReactNode; epoch: string }) {
  const api = useMemo(() => createAccountApi(epoch), [epoch]);
  const { apiGet } = api;
  const location = useLocation();
  const queryClient = useQueryClient();
  const userQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => apiGet<Envelope<User>>('/me').then(result => result.data),
    retry: false,
  });
  const subscription = useSubscription(api, userQuery.isSuccess);
  const workspaceMetadata = /^\/account\/workspaces(?:\/[a-f0-9-]{36}(?:\/members)?)?$/iu.test(location.pathname);
  const accountOnly = workspaceMetadata || ['/account/profile', '/account/sessions', '/account/sign-in-methods'].includes(location.pathname);
  const management = location.pathname === '/account/subscription';
  const financeEnabled = userQuery.isSuccess && subscription.allowed && !management && !workspaceMetadata;
  const workspaceReadsEnabled = financeEnabled || (userQuery.isSuccess && workspaceMetadata);
  const workspacesQuery = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => apiGet<Envelope<Workspace[]>>('/workspaces').then(result => result.data),
    enabled: workspaceReadsEnabled,
    retry: false,
  });
  const [workspaceId, setWorkspaceIdState] = useState(
    () => localStorage.getItem('kazna:active-workspace') ?? '',
  );
  const [secureDataBlocked, setSecureDataBlocked] = useState(false);
  const [contractBlocked, setContractBlocked] = useState(false);
  const workspaces = useMemo(() => workspacesQuery.data ?? [], [workspacesQuery.data]);
  const workspace =
    workspaces.find(item => item.id === workspaceId) ??
    workspaces.find(item => item.status === 'ACTIVE');

  useEffect(() => {
    if (isCurrentAuthEpoch(epoch) && workspace !== undefined && workspace.id !== workspaceId) {
      localStorage.setItem('kazna:active-workspace', workspace.id);
    }
  }, [epoch, workspace, workspaceId]);

  useEffect(() => {
    const clearFinancialQueries = () => {
      setSecureDataBlocked(true);
      queryClient.removeQueries({
        predicate: query =>
          query.queryKey[0] === 'finance' ||
          query.queryKey[0] === 'cashback' ||
          query.queryKey[0] === 'dashboard' ||
          query.queryKey[0] === 'history' ||
          query.queryKey[0] === 'report',
      });
    };
    const restoreSecureReads = () => {
      setSecureDataBlocked(false);
      void queryClient.invalidateQueries({
        predicate: query => query.queryKey[0] === 'cashback',
      });
    };
    const unsubscribe = onSecureFailure(clearFinancialQueries);
    const unsubscribeSubscription = onSubscriptionConfirmed(restoreSecureReads);
    const unsubscribeContract = onContractFailure(() => setContractBlocked(true));
    window.addEventListener('offline', clearFinancialQueries);
    window.addEventListener('online', restoreSecureReads);
    return () => {
      unsubscribe();
      unsubscribeSubscription();
      unsubscribeContract();
      window.removeEventListener('offline', clearFinancialQueries);
      window.removeEventListener('online', restoreSecureReads);
    };
  }, [queryClient]);


  useSync(financeEnabled ? workspace : undefined, api);

  const value = useMemo<AppContextValue | undefined>(() => {
    if (userQuery.data === undefined || (workspaceReadsEnabled && workspacesQuery.data === undefined)) return undefined;
    return {
      api,
      user: userQuery.data,
      subscriptionEnabled: subscription.snapshot?.enabled,
      workspaces: workspaceReadsEnabled ? workspaces : [],
      workspace: workspaceReadsEnabled ? workspace : undefined,
      setWorkspaceId: id => {
        if (!isCurrentAuthEpoch(epoch)) return;
        queryClient.removeQueries({ queryKey: ['cashback'] });
        setWorkspaceIdState(id);
        localStorage.setItem('kazna:active-workspace', id);
      },
    };
  }, [api, epoch, workspaceReadsEnabled, queryClient, subscription.snapshot?.enabled, userQuery.data, workspacesQuery.data, workspaces, workspace]);

  if (userQuery.isError) {
    // A newer tab's cookies may precede its storage notification. Keep this
    // provider mounted until that notification can bind the new account.
    if (userQuery.error instanceof ApiError && userQuery.error.code === 'AUTH_EPOCH_CHANGED') {
      return <StatusScreen title="Обновляем аккаунт…" />;
    }
    const returnTo = safeReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to={`/auth/email/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  if (contractBlocked && !accountOnly && !management) return <StatusScreen title="Требуется обновление приложения или сервера" />;
  if (userQuery.data && (management || (!subscription.loading && !accountOnly && !subscription.allowed))) {
    return <SubscriptionPage key={epoch} api={api} user={userQuery.data} state={subscription} />;
  }
  if (secureDataBlocked && !accountOnly) {
    return <StatusScreen title="Финансовые данные скрыты" />;
  }
  if (userQuery.isPending || (!accountOnly && subscription.loading) || (workspaceReadsEnabled && workspacesQuery.isPending)) {
    return <StatusScreen title="Открываем Казну…" />;
  }
  if (workspaceReadsEnabled && workspacesQuery.isError) {
    return <StatusScreen title="Не удалось загрузить пространства" />;
  }
  if (value === undefined) return <StatusScreen title="Открываем Казну…" />;
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppData(): AppContextValue {
  const value = useContext(AppContext);
  if (value === undefined) throw new Error('AppDataProvider is missing');
  return value;
}

export function useAccountApi() {
  return useAppData().api;
}

function useSync(workspace: Workspace | undefined, { apiGet }: ReturnType<typeof createAccountApi>): void {
  const queryClient = useQueryClient();
  const revisions = useRef(new Map<string, string>());
  useQuery({
    queryKey: ['sync', workspace?.id],
    enabled: workspace !== undefined,
    queryFn: async () => {
      if (workspace === undefined || document.visibilityState !== 'visible') return null;
      const revision = revisions.current.get(workspace.id) ?? workspace.revision;
      const result = await apiGet<
        Envelope<{ revision: string; changes: { entityType: string }[] }>
      >(`/workspaces/${workspace.id}/sync/changes?afterRevision=${revision}&limit=200`);
      const nextRevision = result.meta.workspaceRevision ?? result.data.revision ?? revision;
      if (nextRevision !== revision || result.data.changes.length > 0) {
        revisions.current.set(workspace.id, nextRevision);
        await Promise.all([
          queryClient.invalidateQueries({
            predicate: query =>
              query.queryKey[0] !== 'sync' && query.queryKey.includes(workspace.id),
          }),
          queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
        ]);
      }
      return result.data;
    },
    refetchInterval: () => (document.visibilityState === 'visible' ? 5_000 : false),
    retry: false,
  });
}

function StatusScreen({
  title,
  actionHref,
  actionLabel,
}: {
  title: string;
  actionHref?: string;
  actionLabel?: string;
}) {
  return (
    <main className="status-screen">
      <img className="brand-mark" src="/icon.svg" alt="" aria-hidden="true" />
      <h1>{title}</h1>
      {actionHref !== undefined && (
        <a className="button button-primary" href={actionHref}>
          {actionLabel}
        </a>
      )}
    </main>
  );
}
